import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Trash2,
  School,
  Mail,
  MessageSquare,
  Users,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { formatDate } from '../utils/appDateTime';
import ConfirmSchoolNameDialog from './schools/ConfirmSchoolNameDialog';
import PermanentDeleteSchoolDialog from './schools/PermanentDeleteSchoolDialog';

const TABS = [
  { id: 'schools', label: 'Schools' },
  { id: 'inquiries', label: 'Inquiries' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'employees', label: 'Employees' },
];

function formatWhen(value) {
  return formatDate(value, { day: '2-digit' }) || '—';
}

function SchoolsBinTab() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState('deactivated_at');
  const [order, setOrder] = useState('desc');
  const [planFilter, setPlanFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [allowPermanentDelete, setAllowPermanentDelete] = useState(false);
  const [restoreSchool, setRestoreSchool] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [deleteSchool, setDeleteSchool] = useState(null);

  const fetchBin = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/schools/bin', {
        params: {
          page,
          per_page: perPage,
          search: search || undefined,
          sort,
          order,
          plan: planFilter || undefined,
        },
      });
      const data = res.data?.data || {};
      setItems(data.items || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to load schools bin',
        variant: 'destructive',
      });
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, sort, order, planFilter, toast]);

  useEffect(() => {
    fetchBin();
  }, [fetchBin]);

  useEffect(() => {
    api
      .get('platform/security-settings')
      .then((res) => {
        setAllowPermanentDelete(!!res.data?.data?.allow_permanent_school_deletion);
      })
      .catch(() => setAllowPermanentDelete(false));
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const handleRestore = async () => {
    if (!restoreSchool?.id) return;
    setRestoring(true);
    try {
      await api.post(`schools/${restoreSchool.id}/restore`, {
        confirm_name: restoreSchool.name,
      });
      toast({
        title: 'School restored',
        description: `${restoreSchool.name} is active again.`,
      });
      setRestoreSchool(null);
      fetchBin();
    } catch (err) {
      toast({
        title: 'Restore failed',
        description: err.response?.data?.message || 'Could not restore school',
        variant: 'destructive',
      });
    } finally {
      setRestoring(false);
    }
  };

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-end">
          <form onSubmit={applySearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, code, email…"
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>
          <div className="flex flex-wrap gap-2">
            <Select
              value={sort}
              onValueChange={(v) => {
                setSort(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deactivated_at">Deactivated date</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="code">Code</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={order}
              onValueChange={(v) => {
                setOrder(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Descending</SelectItem>
                <SelectItem value="asc">Ascending</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Filter by plan"
              className="w-[150px]"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <School className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No deactivated schools</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">School</th>
                  <th className="px-4 py-3 font-medium">Owner</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Deactivated</th>
                  <th className="px-4 py-3 font-medium">Storage</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((school) => (
                  <tr key={school.id} className="border-t border-border hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground">{school.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{school.code || '—'}</p>
                      <p className="text-xs text-muted-foreground">{school.email || '—'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p>{school.school_owner || '—'}</p>
                      <p className="text-xs text-muted-foreground">{school.owner_email || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{school.subscription_plan || 'No plan'}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <p>{formatWhen(school.deactivated_at)}</p>
                      <p className="text-xs text-muted-foreground">
                        by {school.deactivated_by_name || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3">{school.storage_used || '0 B'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => setRestoreSchool(school)}
                        >
                          <ArchiveRestore className="w-3.5 h-3.5" />
                          Restore
                        </Button>
                        {allowPermanentDelete && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-destructive hover:text-destructive"
                            onClick={() => setDeleteSchool(school)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Permanent Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              {total} school{total === 1 ? '' : 's'} · Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmSchoolNameDialog
        open={!!restoreSchool}
        onOpenChange={(o) => !o && setRestoreSchool(null)}
        schoolName={restoreSchool?.name}
        title="Restore school?"
        description={
          <p>
            This will reactivate the school. Login and API access for its users will work again
            based on each user&apos;s existing account status. No data will be lost.
          </p>
        }
        confirmLabel="Restore school"
        confirmVariant="default"
        loading={restoring}
        onConfirm={handleRestore}
      />

      <PermanentDeleteSchoolDialog
        open={!!deleteSchool}
        onOpenChange={(o) => !o && setDeleteSchool(null)}
        school={deleteSchool}
        onDeleted={() => {
          setDeleteSchool(null);
          fetchBin();
        }}
      />
    </>
  );
}

function SoftDeleteBinTab({
  emptyIcon: EmptyIcon,
  emptyLabel,
  fetchPath,
  restorePath,
  columns,
  mapRow,
}) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const fetchBin = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(fetchPath, {
        params: {
          page,
          per_page: perPage,
          search: search || undefined,
        },
      });
      const data = res.data?.data || {};
      setItems(data.items || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      const apiMsg = err.response?.data?.message;
      const status = err.response?.status;
      toast({
        title: 'Error',
        description:
          apiMsg ||
          (status ? `Failed to load bin (HTTP ${status})` : 'Failed to load bin'),
        variant: 'destructive',
      });
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchPath, page, perPage, search, toast]);

  useEffect(() => {
    fetchBin();
  }, [fetchBin]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const handleRestore = async (id) => {
    if (!window.confirm('Restore this item from the Bin?')) return;
    setRestoringId(id);
    try {
      await api.post(restorePath(id));
      toast({ title: 'Restored' });
      fetchBin();
    } catch (err) {
      toast({
        title: 'Restore failed',
        description: err.response?.data?.message || 'Could not restore',
        variant: 'destructive',
      });
    } finally {
      setRestoringId(null);
    }
  };

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-4">
          <form onSubmit={applySearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search…"
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <EmptyIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{emptyLabel}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className="px-4 py-3 font-medium">
                      {col.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const mapped = mapRow(row);
                  return (
                    <tr key={row.id} className="border-t border-border hover:bg-muted/20">
                      {columns.map((col) => (
                        <td key={col.key} className="px-4 py-3">
                          {mapped[col.key]}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            disabled={restoringId === row.id}
                            onClick={() => handleRestore(row.id)}
                          >
                            {restoringId === row.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <ArchiveRestore className="w-3.5 h-3.5" />
                            )}
                            Restore
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              {total} item{total === 1 ? '' : 's'} · Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Bin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawTab = searchParams.get('tab') || 'schools';
  const tab = TABS.some((t) => t.id === rawTab) ? rawTab : 'schools';

  const setTab = (next) => {
    setSearchParams(next === 'schools' ? {} : { tab: next }, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="bin-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-foreground mt-2">Bin</h1>
          <p className="text-muted-foreground">
            Soft-deleted items and inactive employees. Restore them here. Schools can be permanently deleted when enabled in Security settings.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {TABS.map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant={tab === t.id ? 'default' : 'outline'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {tab === 'schools' && <SchoolsBinTab />}
      {tab === 'inquiries' && (
        <SoftDeleteBinTab
          emptyIcon={Mail}
          emptyLabel="No deleted inquiries"
          fetchPath="/crm/inquiries/bin"
          restorePath={(id) => `/crm/inquiries/${id}/restore`}
          columns={[
            { key: 'school', label: 'School' },
            { key: 'contact', label: 'Contact' },
            { key: 'status', label: 'Status' },
            { key: 'deleted', label: 'Deleted' },
          ]}
          mapRow={(row) => ({
            school: (
              <div>
                <p className="font-semibold text-foreground">{row.school_name}</p>
                <p className="text-xs text-muted-foreground">{row.city}, {row.state}</p>
              </div>
            ),
            contact: (
              <div>
                <p>{row.principal_name || '—'}</p>
                <p className="text-xs text-muted-foreground">{row.email || row.contact_number || '—'}</p>
              </div>
            ),
            status: <Badge variant="outline">{row.status || '—'}</Badge>,
            deleted: (
              <div>
                <p>{formatWhen(row.deleted_at)}</p>
                <p className="text-xs text-muted-foreground">by {row.deleted_by_name || '—'}</p>
              </div>
            ),
          })}
        />
      )}
      {tab === 'feedback' && (
        <SoftDeleteBinTab
          emptyIcon={MessageSquare}
          emptyLabel="No deleted feedback"
          fetchPath="/feedback/bin"
          restorePath={(id) => `/feedback/${id}/restore`}
          columns={[
            { key: 'feedback', label: 'Feedback' },
            { key: 'school', label: 'School / User' },
            { key: 'status', label: 'Status' },
            { key: 'deleted', label: 'Deleted' },
          ]}
          mapRow={(row) => ({
            feedback: (
              <div>
                <p className="font-semibold text-foreground">{row.title}</p>
                <p className="text-xs text-muted-foreground font-mono">{row.feedback_code || '—'}</p>
              </div>
            ),
            school: (
              <div>
                <p>{row.school_name || '—'}</p>
                <p className="text-xs text-muted-foreground">{row.user_name || '—'}</p>
              </div>
            ),
            status: <Badge variant="outline">{row.status || '—'}</Badge>,
            deleted: <p>{formatWhen(row.deleted_at)}</p>,
          })}
        />
      )}
      {tab === 'employees' && (
        <SoftDeleteBinTab
          emptyIcon={Users}
          emptyLabel="No inactive employees"
          fetchPath="/crm/employees/bin"
          restorePath={(id) => `/crm/employees/${id}/activate`}
          columns={[
            { key: 'employee', label: 'Employee' },
            { key: 'email', label: 'Email' },
            { key: 'role', label: 'Role / Dept' },
            { key: 'updated', label: 'Deactivated' },
          ]}
          mapRow={(row) => ({
            employee: (
              <div>
                <p className="font-semibold text-foreground">
                  {row.first_name} {row.last_name}
                </p>
                <p className="text-xs text-muted-foreground font-mono">{row.employee_code || '—'}</p>
              </div>
            ),
            email: <p>{row.email || '—'}</p>,
            role: (
              <div>
                <p>{row.crm_role_name || row.role_name || '—'}</p>
                <p className="text-xs text-muted-foreground">{row.crm_department_name || '—'}</p>
              </div>
            ),
            updated: <p>{formatWhen(row.updated_at)}</p>,
          })}
        />
      )}
    </div>
  );
}
